/**
 * Air power (ADR-0065): aircraft based in cities, flying one mission a turn.
 *
 * A strike is three things in order — an enemy interceptor may meet it, the target
 * city's anti-aircraft fires, and whatever is left fights the best defender to the
 * death — and the odds shown before it are exact: the striker's health is carried
 * through the first two as a distribution, and the last is the ordinary combat formula
 * at every health it might arrive with. The roll plays the same steps with dice.
 */

import type { Content, UnitDef } from '../../../content/schema/index.js';
import { cityAt } from '../cities/index.js';
import { atWar } from '../diplomacy/index.js';
import { wrappedHexDistance } from '../path/astar.js';
import { nextFloat, type RngState } from '../rng/index.js';
import { NONE, type City, type GameState, type Unit } from '../state/types.js';
import { allUnits, spareAirCapacity, unitDef, unitIndex } from '../units/index.js';
import { canDefend, defenceFactors, defenceStrength, hitsToKill, roundChance, winChance, type Matchup } from './index.js';

export const isAircraft = (def: UnitDef): boolean => def.domain === 'air';
export const intercepts = (def: UnitDef): boolean => def.abilities.includes('intercept');

/** The city an aircraft is based in: its owner's, on its tile. */
export function baseOf(state: GameState, unit: Unit): City | null {
  const city = cityAt(state, state.map, unitIndex(state.map, unit));
  return city && city.owner === unit.owner ? city : null;
}

/** The carrier an aircraft is aboard, if any (ADR-0066). */
export function carrierOf(state: GameState, unit: Unit): Unit | null {
  if (unit.aboard === NONE) return null;
  const ship = state.units[unit.aboard];
  return ship && ship.owner === unit.owner ? ship : null;
}

/** Whether an aircraft is somewhere it can fly from: one of its cities, or its own carrier. */
export function isBased(state: GameState, unit: Unit): boolean {
  return unit.aboard !== NONE ? carrierOf(state, unit) !== null : baseOf(state, unit) !== null;
}

/** An own carrier on this tile with room on deck, if any. */
export function carrierAt(state: GameState, content: Content, owner: number, index: number): Unit | null {
  return (
    allUnits(state)
      .filter((u) => u.owner === owner && unitIndex(state.map, u) === index && unitDef(content, u).airCapacity > 0)
      .sort((a, b) => a.id - b.id)
      .find((u) => spareAirCapacity(state, content, u) > 0) ?? null
  );
}

/** Whether a tile is within an aircraft's reach of its base. */
export function inAirRange(state: GameState, content: Content, unit: Unit, index: number): boolean {
  const range = unitDef(content, unit).airRange ?? 0;
  const distance = wrappedHexDistance(state.map, unitIndex(state.map, unit), index);
  return distance >= 1 && distance <= range;
}

/**
 * Who a strike on this tile fights: the enemy defender leaving the striker the worst
 * odds — ground or sea units by the tile, and only when there are none, aircraft parked
 * there. Nobody: nothing to strike.
 */
export function strikeDefender(state: GameState, content: Content, striker: Unit, index: number): Unit | null {
  const def = unitDef(content, striker);
  const domain = content.terrain.get(state.map.terrain[index]!).water ? 'sea' : 'land';
  const enemies = allUnits(state).filter(
    (u) => u.owner !== striker.owner && unitIndex(state.map, u) === index && atWar(state, striker.owner, u.owner) && canDefend(unitDef(content, u)),
  );
  const grounded = enemies.filter((u) => unitDef(content, u).domain === domain);
  const pool = grounded.length > 0 ? grounded : enemies.filter((u) => isAircraft(unitDef(content, u)));
  let best: Unit | null = null;
  let bestOdds = 2;
  for (const unit of pool) {
    const odds = winChance(
      roundChance(def.attack, defenceStrength(state, content, unit, index, def)),
      hitsToKill(content, striker.hp),
      hitsToKill(content, unit.hp),
    );
    if (odds < bestOdds || (odds === bestOdds && best !== null && unit.id < best.id)) {
      bestOdds = odds;
      best = unit;
    }
  }
  return best;
}

/**
 * The enemy fighter that would meet a strike on this tile: one based in its own city,
 * at war with the striker, with the tile within its range — the strongest, then the
 * healthiest, then the oldest.
 */
export function interceptorFor(state: GameState, content: Content, striker: Unit, index: number): Unit | null {
  let best: Unit | null = null;
  for (const unit of allUnits(state)) {
    if (unit.owner === striker.owner || !atWar(state, striker.owner, unit.owner)) continue;
    const def = unitDef(content, unit);
    if (!intercepts(def) || !isBased(state, unit)) continue;
    if (wrappedHexDistance(state.map, unitIndex(state.map, unit), index) > (def.airRange ?? 0)) continue;
    if (!best) {
      best = unit;
      continue;
    }
    const b = unitDef(content, best);
    if (def.attack > b.attack || (def.attack === b.attack && (unit.hp > best.hp || (unit.hp === best.hp && unit.id < best.id)))) best = unit;
  }
  return best;
}

/** A city's anti-aircraft, as the percent chance each shot hits. */
export function antiAirOf(content: Content, city: City | null): number {
  if (!city) return 0;
  let best = 0;
  for (const id of city.buildings) best = Math.max(best, content.buildings.get(id).effects.antiAir ?? 0);
  return best;
}

/** Everything a strike would meet, and its exact chances. */
export interface StrikePlan {
  striker: Unit;
  at: number;
  defender: Unit;
  interceptor: Unit | null;
  /** Percent per shot. */
  antiAir: number;
  /** Chance the striker is lost before it reaches the target. */
  lostOnTheWay: number;
  /** Chance it reaches the target and wins. */
  odds: number;
  /** The fight at the target, at the striker's present health. */
  matchup: Matchup;
}

type Dist = Map<number, number>;

function addTo(dist: Dist, hp: number, p: number): void {
  if (p > 0) dist.set(hp, (dist.get(hp) ?? 0) + p);
}

/** The striker's health after an interception: up to `rounds` rounds, stopping when either falls. */
export function afterInterception(content: Content, strikerHp: number, interceptorHp: number, p: number): Dist {
  const damage = content.rules.combat.damagePerRound;
  // Joint distribution of (striker hp, interceptor hp), keyed "s:i".
  let joint = new Map<string, number>([[`${strikerHp}:${interceptorHp}`, 1]]);
  for (let round = 0; round < content.rules.combat.interceptRounds; round++) {
    const next = new Map<string, number>();
    for (const [key, chance] of joint) {
      const [s, i] = key.split(':').map(Number) as [number, number];
      if (s <= 0 || i <= 0) {
        next.set(key, (next.get(key) ?? 0) + chance);
        continue;
      }
      const hit = `${Math.max(0, s - damage)}:${i}`;
      const miss = `${s}:${Math.max(0, i - damage)}`;
      next.set(hit, (next.get(hit) ?? 0) + chance * p);
      next.set(miss, (next.get(miss) ?? 0) + chance * (1 - p));
    }
    joint = next;
  }
  const out: Dist = new Map();
  for (const [key, chance] of joint) addTo(out, Number(key.split(':')[0]), chance);
  return out;
}

/** The striker's health after anti-aircraft fire. */
export function afterAntiAir(content: Content, before: Dist, percent: number): Dist {
  const damage = content.rules.combat.damagePerRound;
  let dist = before;
  for (let shot = 0; shot < content.rules.combat.antiAirShots && percent > 0; shot++) {
    const next: Dist = new Map();
    for (const [hp, chance] of dist) {
      if (hp <= 0) {
        addTo(next, 0, chance);
        continue;
      }
      addTo(next, Math.max(0, hp - damage), (chance * percent) / 100);
      addTo(next, hp, (chance * (100 - percent)) / 100);
    }
    dist = next;
  }
  return dist;
}

/** What striking this tile would mean, or null if there is nothing there to strike. */
export function planStrike(state: GameState, content: Content, striker: Unit, at: number): StrikePlan | null {
  const def = unitDef(content, striker);
  const defender = strikeDefender(state, content, striker, at);
  if (!defender) return null;
  const interceptor = interceptorFor(state, content, striker, at);
  const city = cityAt(state, state.map, at);
  const antiAir = city && city.owner !== striker.owner ? antiAirOf(content, city) : 0;

  let dist: Dist = new Map([[striker.hp, 1]]);
  if (interceptor) {
    dist = afterInterception(content, striker.hp, interceptor.hp, roundChance(unitDef(content, interceptor).attack, def.defence));
  }
  dist = afterAntiAir(content, dist, antiAir);

  const defence = defenceStrength(state, content, defender, at, def);
  const p = roundChance(def.attack, defence);
  let lost = 0;
  let odds = 0;
  for (const [hp, chance] of dist) {
    if (hp <= 0) lost += chance;
    else odds += chance * winChance(p, hitsToKill(content, hp), hitsToKill(content, defender.hp));
  }
  return { striker, at, defender, interceptor, antiAir, lostOnTheWay: lost, odds, matchup: strikeMatchup(state, content, striker, defender, at) };
}

/** The fight at the target between this striker, at its health now, and this defender. */
export function strikeMatchup(state: GameState, content: Content, striker: Unit, defender: Unit, at: number): Matchup {
  const def = unitDef(content, striker);
  const defence = defenceStrength(state, content, defender, at, def);
  return {
    attacker: striker,
    defender,
    attack: def.attack,
    defence,
    baseDefence: unitDef(content, defender).defence,
    factors: defenceFactors(state, content, defender, at, def),
    odds: winChance(roundChance(def.attack, defence), hitsToKill(content, striker.hp), hitsToKill(content, defender.hp)),
  };
}

/** Roll the interception: rounds until it ends or someone falls. Mutates nothing. */
export function rollInterception(
  content: Content,
  strikerHp: number,
  interceptorHp: number,
  p: number,
  rng: RngState,
): { strikerHp: number; interceptorHp: number } {
  const damage = content.rules.combat.damagePerRound;
  let s = strikerHp;
  let i = interceptorHp;
  for (let round = 0; round < content.rules.combat.interceptRounds && s > 0 && i > 0; round++) {
    if (nextFloat(rng) < p) s -= damage;
    else i -= damage;
  }
  return { strikerHp: Math.max(0, s), interceptorHp: Math.max(0, i) };
}

/** Roll the anti-aircraft shots. Returns the hits. */
export function rollAntiAir(content: Content, percent: number, rng: RngState): number {
  let hits = 0;
  for (let shot = 0; shot < content.rules.combat.antiAirShots && percent > 0; shot++) if (nextFloat(rng) * 100 < percent) hits++;
  return hits;
}
